import { useMutation, useQuery } from 'react-query';
import { MainContent, EmptyInfo, ScanBtn } from './styles';
import { Box, Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { canScan, scanBySlug } from 'services/asm-service';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData';
import { TabAsset } from './TabAsset';
import { TabCert } from './TabCert';
import { TabVulnerability } from './TabVulnerability';
import { TabPanel } from 'Components/TabPanel';
import { PageHelper } from 'Components/PageHelper';
import { useTranslation } from 'react-i18next';

export const AsmDashboard = () => {
  const { t } = useTranslation(['asm']);
  const [tabPosition, setTabPosition] = useState(0);
  const selectedTenant = useSelector(useAuthTenant);

  const { data: cantScan, isLoading: canScanLoading } = useQuery(['getCanScan'], async () => {
    if (selectedTenant.slug === undefined) return true;

    const response = await canScan(selectedTenant.slug);
    if (response.data) {
      return !response.data.canScan;
    }
    return true;
  });

  const mutation = useMutation(
    async () => {
      const response = await scanBySlug(selectedTenant.slug);
      // const response = await scanBySlug(selectedTenant.slug);
      if (response.status === 200) {
        Swal.fire(
          t('asm:dashboard.scanStartedTitle'),
          t('asm:dashboard.scanStartedText'),
          'success',
        );
        setButtonDisabled(true);
      }
    },
    {
      onError: () => {
        Swal.fire({
          icon: 'info',
          title: t('asm:dashboard.scanRunningTitle'),
          text: t('asm:dashboard.scanRunningText'),
        });
      },
      onSuccess: () => {},
    },
  );

  const [isButtonDisabled, setButtonDisabled] = useState(false);

  return (
    <div className="mt-3 px-3" data-tour="asm-dashboard">
      {!canScanLoading && !cantScan && (
        <EmptyInfo data-tour="empty-state">
          <NoData description={t('asm:dashboard.emptyDescription')} />
          <p>{t('asm:dashboard.emptyInstruction')}</p>
          <div data-tour="scan-button">
          <ScanBtn
            disabled={cantScan && isButtonDisabled}
            onClick={() => {
              Swal.fire({
                title: t('asm:dashboard.scanConfirmTitle'),
                text: t('asm:dashboard.scanConfirmInfo'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: t('asm:dashboard.scanConfirmYes'),
                cancelButtonText: t('asm:dashboard.scanConfirmCancel'),
              }).then(result => {
                if (result.isConfirmed) {
                  mutation.mutate();
                }
              });
            }}
          >
            {t('asm:dashboard.scanButton')}
          </ScanBtn>
          </div>
        </EmptyInfo>
      )}
      {cantScan && (
        <MainContent>
          <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', marginBottom: '24px' }} data-tour="tabs-container">
            <Tabs value={tabPosition} onChange={(_, newValue) => setTabPosition(newValue)}>
              <Tab
                label={t('asm:dashboard.tabAssets')}
                id="tab-assets"
                aria-controls="tabpanel-assets"
                style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                data-tour="tab-assets"
              />
              <Tab
                label={t('asm:dashboard.tabCertificates')}
                id="tab-certificates"
                aria-controls="tabpanel-certificates"
                style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                data-tour="tab-certificates"
              />
              <Tab
                label={t('asm:dashboard.tabVulnerabilities')}
                id="tab-vulnerabilities"
                aria-controls="tabpanel-vulnerabilities"
                style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                data-tour="tab-vulnerabilities"
              />
            </Tabs>
          </Box>
          <TabPanel value={tabPosition} index={0} id="tabpanel-assets" ariaLabel="tab-assets">
            <TabAsset />
          </TabPanel>
          <TabPanel value={tabPosition} index={1} id="tabpanel-certificates" ariaLabel="tab-certificates">
            <TabCert />
          </TabPanel>
          <TabPanel value={tabPosition} index={2} id="tabpanel-vulnerabilities" ariaLabel="tab-vulnerabilities">
            <TabVulnerability />
          </TabPanel>
        </MainContent>
      )}
      <PageHelper 
        steps={cantScan ? tourStepsWithData(setTabPosition) : tourStepsEmpty} 
        tourKey="asm-dashboard" 
      />
    </div>
  );
};

const tourStepsEmpty = [
  {
    target: 'body',
    content: 'Bem-vindo ao ASM (Attack Surface Management)! Esta ferramenta monitora e analisa automaticamente seus domínios em busca de ativos, certificados e vulnerabilidades.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="empty-state"]',
    content: 'Esta é a tela inicial. Nenhum scan foi executado ainda. É necessário iniciar o primeiro scan para começar a monitorar seus domínios.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="scan-button"]',
    content: 'Use este botão para iniciar uma análise completa de todos os domínios cadastrados. O processo leva em média 1 dia para ser concluído.',
    disableScrolling: true,
  },
  {
    target: 'body',
    content: 'Tour concluído! Após iniciar o scan, você poderá visualizar os resultados em 3 abas: Ativos, Certificados e Vulnerabilidades.',
    disableScrolling: true,
    placement: 'center',
  },
];

const tourStepsWithData = (setTabPosition) => [
  {
    target: 'body',
    content: 'Bem-vindo ao ASM (Attack Surface Management)! Aqui você visualiza os resultados das análises dos seus domínios.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="tabs-container"]',
    content: 'Os resultados são organizados em 3 abas: Ativos (recursos descobertos), Certificados (SSL/TLS) e Vulnerabilidades (riscos identificados).',
    disableScrolling: true,
  },
  // Aba Ativos
  {
    target: '[data-tour="tab-assets"]',
    content: 'Vamos explorar a aba Ativos, onde você visualiza todos os recursos descobertos durante o scan.',
    disableScrolling: true,
    beforeEnter: async () => {
      setTabPosition(0);
      await new Promise(resolve => setTimeout(resolve, 500));
    },
  },
  {
    target: '[data-tour="charts-container"]',
    content: 'Estes gráficos mostram a distribuição de CDN (Content Delivery Network) e WAF (Web Application Firewall) encontrados nos seus ativos.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="cdn-chart"]',
    content: 'O gráfico CDN mostra quais provedores de rede de entrega de conteúdo estão sendo utilizados (Cloudflare, Akamai, etc.).',
    disableScrolling: false,
  },
  {
    target: '[data-tour="waf-chart"]',
    content: 'O gráfico WAF exibe os firewalls de aplicação web detectados, que protegem seus ativos contra ataques.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="assets-table"]',
    content: 'Esta tabela lista todos os ativos descobertos, incluindo subdomínios, endereços IP, tecnologias utilizadas e status de cada recurso.',
    disableScrolling: false,
  },
  // Transição para Certificados
  {
    target: '[data-tour="tab-certificates"]',
    content: 'Agora vamos para a aba Certificados, onde você monitora os certificados SSL/TLS dos seus domínios.',
    disableScrolling: true,
    beforeEnter: async () => {
      setTabPosition(1);
      await new Promise(resolve => setTimeout(resolve, 500));
    },
  },
  {
    target: '[data-tour="cert-overview"]',
    content: 'Esta seção apresenta uma visão geral dos certificados: contador total e distribuição por emissor.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="cert-counter"]',
    content: 'Este card mostra o número total de certificados SSL/TLS encontrados nos seus domínios.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="cert-issuers"]',
    content: 'O gráfico de Emissores mostra quais autoridades certificadoras (Let\'s Encrypt, DigiCert, etc.) emitiram os certificados dos seus domínios.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="cert-table"]',
    content: 'Esta tabela lista todos os certificados com informações detalhadas: domínio, emissor, datas de validade, status de expiração e mais.',
    disableScrolling: false,
  },
  // Transição para Vulnerabilidades
  {
    target: '[data-tour="tab-vulnerabilities"]',
    content: 'Por fim, a aba Vulnerabilidades, onde você monitora todas as falhas de segurança identificadas.',
    disableScrolling: true,
    beforeEnter: async () => {
      setTabPosition(2);
      await new Promise(resolve => setTimeout(resolve, 300));
    },
  },
  {
    target: '[data-tour="vuln-overview"]',
    content: 'Esta seção apresenta uma visão geral: contador total de vulnerabilidades e distribuição por severidade.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="vuln-counter"]',
    content: 'Este card mostra o número total de vulnerabilidades encontradas nos seus ativos.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="severity-chart"]',
    content: 'O gráfico de Severidades mostra a distribuição das vulnerabilidades por nível de criticidade: Crítica, Alta, Média, Baixa.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="vuln-chart"]',
    content: 'Este gráfico detalha os tipos de vulnerabilidades encontradas, mostrando a quantidade de cada tipo identificado.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="vuln-table"]',
    content: 'Esta tabela lista todas as vulnerabilidades com informações detalhadas: título, descrição, severidade, domínio afetado e recomendações de correção.',
    disableScrolling: false,
  },
  {
    target: 'body',
    content: 'Tour concluído! O ASM monitora continuamente sua superfície de ataque. Priorize a correção das vulnerabilidades críticas e monitore os certificados regularmente.',
    disableScrolling: true,
    placement: 'center',
  },
];
